import type { JSX } from "react";
import { FC } from "react";
import { P1, P2 } from "../atoms/Paragraph";
import { HeadingSize, H2 } from "../atoms/Heading";
import styled from "styled-components";
import { color as themeColor } from "@styles";
import { CheckList } from "./CheckList";
import { StandardSection } from "../templates/StandardSection";

interface footnote {
  number: number;
  copy: string;
}

interface Props {
  pt?: string;
  pb?: string;
  title?: string | JSX.Element;
  titleAlignment?: "left" | "center" | "right";
  body?: JSX.Element;
  points: string[] | JSX.Element[];
  disclaimer?: string;
  footnote?: footnote[];
  background?: string;
  isReversed?: boolean;
  boldPoints?: boolean;
  checklistTitle?: string;
}

export const BulletPoints: FC<React.PropsWithChildren<Props>> = ({
  pt,
  pb,
  title,
  titleAlignment = "left",
  body,
  points,
  disclaimer,
  background,
  isReversed,
  boldPoints = false,
  footnote,
  checklistTitle,
}) => {
  const text = (
    <Text>
      {title ? (
        <Header align={titleAlignment}>
          {typeof title === "string" ? (
            <H2 size={HeadingSize.H1} align="inherit">
              {title}
            </H2>
          ) : (
            title
          )}
        </Header>
      ) : null}
      {body}
    </Text>
  );
  const list = (
    <div style={{ flex: 1, flexBasis: "100%" }}>
      <CheckList
        pt="0"
        items={points}
        textColor={themeColor.B1100}
        textBold={boldPoints}
        iconColor="white"
        backgroundColor={themeColor.S1100}
        checklistTitle={checklistTitle}
      />
      {footnote && footnote.length > 0 ? (
        <>
          {footnote.map((note, index) => (
            <P2 key={index} pt="2.5rem" color={themeColor.B2100} style={{ maxWidth: "27.5rem" }}>
              <sup>{note.number.toString()}</sup>
              {note.copy}
            </P2>
          ))}
        </>
      ) : null}
      {disclaimer ? (
        <P2 pt="2.5rem" style={{ maxWidth: "27.5rem" }}>
          *{disclaimer}
        </P2>
      ) : null}
    </div>
  );
  return (
    <StandardSection pt={pt} pb={pb} background={background}>
      <FlexRow>
        {isReversed ? (
          <>
            {list}
            <Gutter />
            {text}
          </>
        ) : (
          <>
            {text}
            <Gutter />
            {list}
          </>
        )}
      </FlexRow>
    </StandardSection>
  );
};

export const InlineBulletPoints: FC<
  React.PropsWithChildren<{
    pt?: string;
    pb?: string;
    points: (string | string[] | JSX.Element)[];
    textColor?: string;
  }>
> = ({ pt, pb, points, textColor = themeColor.S3100 }) => {
  return (
    <CheckList
      pt={pt}
      pb={pb}
      items={points}
      textColor={textColor}
      iconColor="white"
      backgroundColor={themeColor.S1100}
    />
  );
};

const Bullet = ({ text }: { text?: string }) => (
  <LI>
    <Circle>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="10"
        viewBox="0 0 14 10"
        fill="none"
      >
        <path
          d="M12.3333 1L4.92221 9L1.66663 5.4"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Circle>
    {!text ? null : (
      <P1 style={{ display: "inline", padding: 0, margin: "0 0 0 1.5rem", fontSize: "1.2rem" }}>
        {text}
      </P1>
    )}
  </LI>
);

const FlexRow = styled.div`
  display: flex;

  @media only screen and (max-width: 52rem) {
    display: block;
  }
`;

const Text = styled.div`
  width: 100%;
  flex: 1;
  flex-basis: 100%;
`;

const Header = styled.div<{ align?: "left" | "center" | "right" }>`
  text-align: ${(p) => p.align || "left"};

  @media only screen and (max-width: 52rem) {
    text-align: left;
  }
`;

const Gutter = styled.div`
  flex-shrink: 0;
  height: 0;
  width: 8rem;
  height: 2.5rem;

  @media only screen and (max-width: 60rem) {
    width: 4rem;
  }
`;

const LI = styled.li`
  margin-top: 1.25rem;
  display: flex;
  align-items: center;
  line-height: 2rem;
  list-style: none;

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type {
    margin-bottom: 1.25rem;
  }
`;

const Circle = styled.div`
  border-radius: 50%;
  font-size: 1.45rem;
  background-color: #00ed9d;
  color: #fff;
  width: 1.5rem;
  height: 1.5rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-top: 0.55rem;
  padding-bottom: 0.5rem;
  padding-left: 0.55rem;
  padding-right: 0.475rem;
  transform: translateY(-0.2rem);
`;
